// --- START OF FILE SocialMediaSidebar.js ---
// src/components/SocialMediaSidebar.js
import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faShareNodes,
  faFlask,
  faDatabase,
  faCog,
  faBoxOpen,
  faBuilding,
  faSearch,
  faDollar,
  faCrown,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import './SocialMediaSidebar.css';
import { useData } from '../context/DataContext';

const SocialMediaSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    userProfile, // Needed to check role
    setIsProductModalOpen,
    setIsLlmAdminModalOpen,
    setIsLlmConfigModalOpen,
    setIsCompanyModalOpen,
    setIsUserProfileModalOpen, // <-- Add setter for User Profile Modal
    setIsAdminTasksModalOpen, // <-- Add setter for Admin Tasks Modal
    setIsDiscoverySettingsModalOpen,
    setIsAnalyticsModalOpen,
    fetchProducts,
    fetchCompanyData,
    setIsSuperAdminModalOpen,
  } = useData();

  const isAdmin = userProfile?.role === 'Admin';

  const isSuperAdmin = userProfile?.isSuperAdmin === true;

  // Fetch products and company data when the component mounts if the user is an admin
  useEffect(() => {
    if (isAdmin) {
      console.log(
        '[SocialMediaSidebar] Admin detected, fetching products & company data.',
      );
      fetchProducts();
      fetchCompanyData();
    }
  }, [isAdmin, fetchProducts, fetchCompanyData]);

  const handleProductIconClick = () => {
    if (isAdmin) {
      console.log(
        '[SocialMediaSidebar] Product icon clicked by Admin. Opening modal.',
      );
      setIsProductModalOpen(true);
    } else {
      console.warn(
        '[SocialMediaSidebar] Product icon clicked by non-admin user.',
      );
    }
  };

  const handleAdminLlmIconClick = () => {
    if (isAdmin) {
      console.log(
        '[SocialMediaSidebar] Database LLM icon clicked by Admin. Opening modal.',
      );
      setIsLlmAdminModalOpen(true);
    } else {
      console.warn(
        '[SocialMediaSidebar] Database LLM icon clicked by non-admin user.',
      );
    }
  };

  const handleUserLlmIconClick = () => {
    console.log(
      '[SocialMediaSidebar] User LLM config icon clicked. Opening modal.',
    );
    setIsLlmConfigModalOpen(true);
  };

  const handleCompanyIconClick = () => {
    if (isAdmin) {
      console.log(
        '[SocialMediaSidebar] Company icon clicked by Admin. Opening modal.',
      );
      setIsCompanyModalOpen(true);
    } else {
      console.warn(
        '[SocialMediaSidebar] Company icon clicked by non-admin user.',
      );
    }
  };

  // Handler for User Profile Icon
  const handleUserProfileIconClick = () => {
    console.log(
      '[SocialMediaSidebar] User Profile icon clicked. Opening modal.',
    );
    setIsUserProfileModalOpen(true); // Open the User Profile modal
  };

  // Handler for Settings Icon (Admin Tasks Modal)
  const handleSettingsIconClick = () => {
    if (isAdmin) {
      console.log(
        '[SocialMediaSidebar] Settings icon clicked by Admin. Opening Admin Tasks modal.',
      );
      setIsAdminTasksModalOpen(true); // Open the new Admin Tasks modal
    } else {
      console.warn(
        '[SocialMediaSidebar] Settings icon clicked by non-admin user. No action.',
      );
      // Optionally, provide feedback to non-admin users if they click
      // e.g., navigate to a user settings page or show a message.
      // For now, it's a silent non-action for non-admins.
    }
  };

  const handleDiscoverySettingsClick = () => {
    console.log(
      '[SocialMediaSidebar] Discovery Settings icon clicked. Opening modal.',
    );
    setIsDiscoverySettingsModalOpen(true);
  };

  const handleAnalyticsIconClick = () => {
    console.log(
      '[SocialMediaSidebar] Analytics Dashboard icon clicked. Opening modal.',
    );
    setIsAnalyticsModalOpen(true);
  };

  const handleSuperAdminClick = () => {
    setIsSuperAdminModalOpen(true);
  };

  return (
    <div className="social-media-sidebar">
      {/* --- User Profile Icon (Existing, update handler) --- */}
      <button
        className="sidebar-item"
        title="Your Profile" // Updated title
        onClick={handleUserProfileIconClick} // Use the new handler
      >
        <FontAwesomeIcon icon={faUser} size="2x" />
      </button>
      {/* --- End User Profile Icon --- */}

      <button
        className="sidebar-item"
        title="Social Media Connections"
        onClick={() => {
          navigate('/social-media', {
            state: { from: location },
            replace: false,
          });
        }}
      >
        <FontAwesomeIcon icon={faShareNodes} size="2x" />
      </button>
      {/* Settings Icon (Handle differently for Admin) */}
      <button
        className="sidebar-item"
        title={isAdmin ? 'Admin Tasks' : 'Settings'} // Update title based on role
        onClick={isAdmin ? handleSettingsIconClick : () => navigate('#')} // New handler for Admin
      >
        <FontAwesomeIcon icon={faCog} size="2x" />
      </button>
      {/* User Discovery Settings (All Users) */}
      <button
        className="sidebar-item discovery-settings-icon"
        title="Manage Discovery Settings"
        onClick={handleDiscoverySettingsClick}
      >
        <FontAwesomeIcon icon={faSearch} size="2x" />
      </button>

      {/* User LLM/Prompt Config (All Users) */}
      <button
        className="sidebar-item llm-config-icon"
        title="Configure LLM Defaults & Prompts"
        onClick={handleUserLlmIconClick}
      >
        <FontAwesomeIcon icon={faFlask} size="2x" />
      </button>
      <button
        className="sidebar-item"
        title="Billing & Usage" // <-- UPDATED NAME
        onClick={handleAnalyticsIconClick}
      >
        <FontAwesomeIcon icon={faDollar} size="2x" />
      </button>

      {/* --- SuperAdmin ONLY Icon --- */}
      {isSuperAdmin && (
        <>
          <button
            className="sidebar-item"
            title="SuperAdmin Dashboard"
            style={{ backgroundColor: '#5c4d00' }} // Make it stand out with a gold/dark background
            onClick={handleSuperAdminClick}
          >
            <FontAwesomeIcon icon={faCrown} size="2x" color="#ffcc00" />
          </button>
          {/* Admin LLM Management Icon */}
          <button
            className="sidebar-item llm-admin-icon"
            title="Manage Available LLMs"
            onClick={handleAdminLlmIconClick}
          >
            <FontAwesomeIcon icon={faDatabase} size="2x" />
          </button>
        </>
      )}

      {/* --- Admin Only Icons --- */}
      {isAdmin && (
        <>
          {/* Product Icon */}
          <button
            className="sidebar-item product-icon"
            title="Manage Products"
            onClick={handleProductIconClick}
          >
            <FontAwesomeIcon icon={faBoxOpen} size="2x" />
          </button>
          {/* Company Icon */}
          <button
            className="sidebar-item company-icon"
            title="Manage Company Details"
            onClick={handleCompanyIconClick}
          >
            <FontAwesomeIcon icon={faBuilding} size="2x" />
          </button>
        </>
      )}
    </div>
  );
};

export default SocialMediaSidebar;
